import { getEventPropsBuilder, toSnakeCase } from '@/tracking/amplitude/utils';
import type { RewardsHubSharedState, RewardsHubTrackSendEvent } from './rewards-hub';
import { trackRewardsHubAmplitude, mapPromoBaseContextToAmplitudeProps } from './rewards-hub';
import { REWARDS_HUB_AMP_EVENT, REWARDS_MODULE } from './event-names';

type PromoClickEvent = {
    type: 'BUTTON_CLICK' | 'CARD_CLICK';
    label: string;
    href?: string;
    promoCode?: string;
};

export enum PromoApiEventType {
    REQUEST = 'API_REQUEST',
    SUCCESS = 'API_SUCCESS',
    ERROR = 'API_ERROR'
}

type PromoApiEvent = {
    type: PromoApiEventType;
    description: string;
    promoCode?: string;
    promoName?: string;
    message?: string;
};

type PromoGenericError = {
    type: 'GENERIC_ERROR';
    message: string;
};

export type PromoEventModule =
    | typeof REWARDS_MODULE.PROMO_HUB_HOME
    | typeof REWARDS_MODULE.PROMO_HUB_OFFER
    | typeof REWARDS_MODULE.HERO_CAROUSEL;

export type RewardsHubPromoEvent = PromoClickEvent | PromoApiEvent | PromoGenericError | { type?: string };

const mapApiEventPromoProps = (apiEvent: PromoApiEvent) =>
    mapPromoBaseContextToAmplitudeProps({
        promoId: apiEvent.promoCode,
        promoName: apiEvent.promoName
    });

export const trackRewardsHubPromoEventToAmplitude = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    event: RewardsHubPromoEvent;
    sharedState?: RewardsHubSharedState | null;
    module?: PromoEventModule;
}) => {
    const { sendEvent, event, sharedState, module } = args;
    const type = event?.type;

    let getEventProps: ReturnType<typeof getEventPropsBuilder> | undefined;
    const getBaseProps = (_module: string | undefined, e: PromoApiEvent | undefined) => ({
        ...(_module ? { Module: _module } : {}),
        ...(e ? mapApiEventPromoProps(e) : {})
    });

    switch (type) {
        case PromoApiEventType.REQUEST:
            getEventProps = getEventPropsBuilder((_module, e) => ({
                event: REWARDS_HUB_AMP_EVENT.PROMO_OPT_IN_SUBMITTED,
                ...getBaseProps(_module, e as PromoApiEvent)
            }));
            break;
        case PromoApiEventType.SUCCESS:
            getEventProps = getEventPropsBuilder((_module, e) => ({
                event: REWARDS_HUB_AMP_EVENT.PROMO_OPT_IN_SUCCESSFUL,
                ...getBaseProps(_module, e as PromoApiEvent)
            }));
            break;
        case PromoApiEventType.ERROR:
            getEventProps = getEventPropsBuilder((_module, e) => ({
                event: REWARDS_HUB_AMP_EVENT.PROMO_OPT_IN_FAILED,
                ...getBaseProps(_module, e as PromoApiEvent),
                ...((e as PromoApiEvent).message ? { 'Error Reason': toSnakeCase((e as PromoApiEvent).message!) } : {})
            }));
            break;
        case 'GENERIC_ERROR':
            getEventProps = getEventPropsBuilder((_module, e) => ({
                event: REWARDS_HUB_AMP_EVENT.PROMO_GENERIC_ERROR,
                Message: (e as PromoGenericError).message
            }));
            break;
        default:
            break;
    }
    if (!getEventProps) {
        return;
    }

    const eventProps = getEventProps(module, event);
    trackRewardsHubAmplitude({
        sendEvent,
        event: eventProps.event,
        sharedState,
        props: eventProps
    });
};
